import { _, helpers, mui, React, ts, ui, useSelector } from '_core';

import { useMemo } from 'react';

import { Params } from './types';
import { prepareBucketedRegimeData } from '../../common/utils/signal-utils';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  fullScreen: boolean;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  file: string;
  params: Params;
  context?: ts.types.signal.SignalAnalysisContext;
  title: string;
};

const Chart = ({ id, readOnly, fullScreen, loadWidgetData, file, params, context, title }: ChartProps) => {
  const [data, setData] = React.useState<ts.types.widgets.HeatMapData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);
  const [selectedTab, setSelectedTab] = React.useState<'returns' | 'count' | 'risk' | 'risk_adjusted_returns'>(
    'returns'
  );

  const resources = useSelector((state) => state.resources);

  const signals = useSelector((state) => state.resources.signals);

  const currSig = React.useMemo(() => {
    return signals.find((s) => s.id == context.signal_id);
  }, [context.signal_id]);

  const loadData = async (tab: 'returns' | 'count' | 'risk' | 'risk_adjusted_returns') => {
    try {
      const response = await loadWidgetData({
        data: {
          file,
          query: ['tab', 'equals', tab],
        },
      });

      if (response) {
        setData(response.data as ts.types.widgets.HeatMapData);
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (file) loadData(selectedTab);
    return () => setData(null);
  }, [file, selectedTab]);

  const yAxisLabel = React.useMemo(() => {
    if (params.buckets?.type == ts.enums.BUCKET_TYPES_ENUM.SIGNAL)
      return signals.find((s) => s.id == params.buckets.signal_id).name;
    return undefined;
  }, []);

  const getTabName = (t = selectedTab) => {
    if (t == 'count') return 'Avg Count';
    if (t == 'returns') return 'Annualized Returns';
    if (t == 'risk') return 'Annualized Risk';
    if (t == 'risk_adjusted_returns') return 'Annualized Risk Adjusted Return';
    return _.startCase(t);
  };

  const getTabs = () => {
    return (
      <mui.core.Tabs
        indicatorColor="primary"
        textColor="primary"
        value={selectedTab}
        onChange={(e, newTab) => setSelectedTab(newTab)}
        aria-label="Tabs"
      >
        {['returns', 'count', 'risk', 'risk_adjusted_returns'].map((tab) => (
          <mui.core.Tab key={tab} label={getTabName(tab as any)} value={tab} />
        ))}
      </mui.core.Tabs>
    );
  };

  const formattedData = useMemo(() => {
    if (_.isNil(data)) return [];
    return prepareBucketedRegimeData(data, false, params.quantiles, params.buckets, resources);
  }, [data]);

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }}>
      {getTabs()}
      <mui.core.Box p={3} flex="1">
        <ui.HeatmapChart
          id={id}
          numberFormat={selectedTab === 'returns' || selectedTab === 'risk' ? '#.##%' : '#.##'}
          data={formattedData}
          xAxisField="bucket1"
          yAxisField="bucket2"
          xAxisLabel={currSig.name}
          yAxisLabel={yAxisLabel}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          exportTitle={title}
          fullScreen={fullScreen}
          readOnly={readOnly}
          labelValues
        />
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
